import { Link } from '@heidi/design-system/link';
import { TypographyCaption, TypographyP2Bold } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { ExternalLink, ImageOff } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { type MessageDescriptor, useIntl } from 'react-intl';
import type { VisualDxGalleryImage, VisualDxGalleryPayload } from '../../contracts';
import { chipClass } from './VisualDxDiagnosticFlow.styles';
import { visualDxGalleryMessages as messages } from './VisualDxGallery.messages';

/**
 * Heidi-native VisualDx image gallery (PRD Module 3). Mounted by
 * ``InteractiveWidgetBlock`` for the ``visualdx_gallery`` widget kind when
 * the LLM calls ``get_diagnosis_gallery`` and the BE acks with the parsed,
 * single-page payload.
 *
 * The MCP server returns images only as inline base64 (no CDN URLs) inside a
 * multi-MB HTML document, so the BE parses + paginates to one page of ~10
 * thumbnails before this ever reaches the client. Full browsing (all pages,
 * high-res) happens via the "Open in VisualDx" deep link.
 *
 * Fitzpatrick grouping: the PRD groups the 1-6 skin-type scale into
 * Light (1-2) / Medium (3-4) / Dark (5-6) filter chips. Filtering here is
 * client-side over the returned page only.
 *
 * SaMD / privacy: images are lazy-loaded with ``referrerPolicy="no-referrer"``
 * (mirrors ``VisualDxRankedDifferential`` + ``EvidenceImageBlock``); captions
 * are shown verbatim from VisualDx, never model-generated.
 *
 * Styling: filter chips reuse ``chipClass`` from the diagnostic flow, links
 * are the design-system ``Link``, so the gallery reads as the same VisualDx
 * surface as the flow it usually follows.
 */

interface VisualDxGalleryProps {
  gallery: VisualDxGalleryPayload;
}

type SkinGroup = 'all' | 'light' | 'medium' | 'dark';

const SKIN_GROUPS: { id: SkinGroup; label: MessageDescriptor; types: number[] }[] = [
  { id: 'all', label: messages.groupAll, types: [1, 2, 3, 4, 5, 6] },
  { id: 'light', label: messages.groupLight, types: [1, 2] },
  { id: 'medium', label: messages.groupMedium, types: [3, 4] },
  { id: 'dark', label: messages.groupDark, types: [5, 6] },
];

const containerClass =
  'flex w-full flex-col gap-3 rounded-xl bg-background-secondary p-4 text-text-primary';

const headerRowClass = 'flex items-baseline justify-between gap-2';

const filterRowClass = 'flex flex-wrap gap-1.5';

// The flow's chip, sized down one step so a row of four fits the chat bubble.
const filterChipClass = (active: boolean): string =>
  cn(chipClass(active), 'rounded px-3 py-1.5 text-xs');

const gridClass = 'grid grid-cols-2 gap-2 sm:grid-cols-3';

const tileClass =
  'group flex flex-col overflow-hidden rounded-lg border border-border bg-surface';

const imgClass = 'aspect-square w-full bg-background-tertiary object-cover';

const imgPlaceholderClass = cn(
  imgClass,
  'flex items-center justify-center text-text-tertiary'
);

const tagClass =
  'rounded bg-background-tertiary px-1.5 py-0.5 text-[10px] text-text-tertiary';

const ExternalTextLink = ({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}): JSX.Element => (
  <Link
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    variant="inline"
    className="inline-flex items-center gap-1 self-start"
  >
    <TypographyCaption className="text-text-link hover:underline">
      {children}
    </TypographyCaption>
    <ExternalLink className="size-3 shrink-0 text-text-link" />
  </Link>
);

export const VisualDxGallery = ({ gallery }: VisualDxGalleryProps): JSX.Element => {
  const intl = useIntl();
  const [group, setGroup] = useState<SkinGroup>('all');
  const images = gallery.images;

  const visible = useMemo<VisualDxGalleryImage[]>(() => {
    const page = images ?? [];
    if (group === 'all') return page;
    const allowed = new Set(SKIN_GROUPS.find((g) => g.id === group)?.types ?? []);
    // Keep images whose skin type is in the group; images with an unknown
    // (null) skin type are only shown under "All" so a filter never implies
    // a tone we can't actually confirm.
    return page.filter((img) => img.skin_type != null && allowed.has(img.skin_type));
  }, [images, group]);

  // Only offer a skin-tone chip when at least one image on this page carries
  // that tone — avoids dead filters that always yield an empty grid.
  const availableGroups = useMemo(() => {
    const page = images ?? [];
    const present = new Set(
      page.map((img) => img.skin_type).filter((n): n is number => n != null)
    );
    return SKIN_GROUPS.filter(
      (g) => g.id === 'all' || g.types.some((t) => present.has(t))
    );
  }, [images]);

  if (!images || images.length === 0) {
    return (
      <div className={containerClass}>
        <TypographyCaption className="text-text-secondary">
          {gallery.diagnosis
            ? intl.formatMessage(messages.emptyForDiagnosis, {
                diagnosis: gallery.diagnosis,
              })
            : intl.formatMessage(messages.empty)}
        </TypographyCaption>
        {gallery.visualdx_url ? (
          <ExternalTextLink href={gallery.visualdx_url}>
            {intl.formatMessage(messages.openInVisualDx)}
          </ExternalTextLink>
        ) : null}
      </div>
    );
  }

  return (
    <div className={containerClass}>
      <div className={headerRowClass}>
        <TypographyP2Bold className="min-w-0 truncate text-text-primary">
          {gallery.diagnosis || intl.formatMessage(messages.heading)}
        </TypographyP2Bold>
        <TypographyCaption className="shrink-0 text-text-secondary">
          {intl.formatMessage(messages.imageCount, { count: gallery.total_images })}
          {gallery.total_pages > 1
            ? ` · ${intl.formatMessage(messages.pageOf, {
                page: gallery.page,
                total: gallery.total_pages,
              })}`
            : ''}
        </TypographyCaption>
      </div>

      {availableGroups.length > 1 ? (
        <div
          className={filterRowClass}
          role="group"
          aria-label={intl.formatMessage(messages.filterLabel)}
        >
          {availableGroups.map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => setGroup(g.id)}
              aria-pressed={group === g.id}
              className={filterChipClass(group === g.id)}
            >
              {intl.formatMessage(g.label)}
            </button>
          ))}
        </div>
      ) : null}

      <div className={gridClass}>
        {visible.map((img, idx) => {
          const tile = (
            <>
              {img.thumbnail_url ? (
                // VisualDx returns inline base64 (per-token, no stable CDN
                // URL) so next/image domain whitelisting doesn't apply; a
                // plain lazy <img> matches EvidenceImageBlock.
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={img.thumbnail_url}
                  alt={img.caption ?? ''}
                  className={imgClass}
                  loading="lazy"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className={imgPlaceholderClass} aria-hidden>
                  <ImageOff className="size-5" />
                </div>
              )}
              <div className="flex flex-col gap-1 p-2">
                {img.caption ? (
                  <TypographyCaption
                    className="text-text-secondary"
                    style={{
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 2,
                      overflow: 'hidden',
                    }}
                    title={img.caption}
                  >
                    {img.caption}
                  </TypographyCaption>
                ) : null}
                <div className="flex flex-wrap gap-1">
                  {img.skin_type != null ? (
                    <span className={tagClass}>
                      {intl.formatMessage(messages.skinType, { type: img.skin_type })}
                    </span>
                  ) : null}
                  {img.body_location ? (
                    <span className={tagClass}>{img.body_location}</span>
                  ) : null}
                </div>
              </div>
            </>
          );
          return img.visualdx_url ? (
            <Link
              key={`${idx}-${img.visualdx_url}`}
              href={img.visualdx_url}
              target="_blank"
              rel="noopener noreferrer"
              variant="inline"
              className={cn(
                tileClass,
                'no-underline transition-colors hover:border-border-hover hover:no-underline'
              )}
              aria-label={
                img.caption
                  ? intl.formatMessage(messages.tileLabelWithCaption, {
                      caption: img.caption,
                    })
                  : intl.formatMessage(messages.tileLabel)
              }
            >
              {tile}
            </Link>
          ) : (
            <div key={idx} className={tileClass}>
              {tile}
            </div>
          );
        })}
      </div>

      {group !== 'all' && visible.length === 0 ? (
        <TypographyCaption className="text-text-secondary">
          {intl.formatMessage(messages.noneForTone)}
        </TypographyCaption>
      ) : null}

      <div className="flex items-center justify-between">
        <TypographyCaption className="text-text-tertiary">
          {intl.formatMessage(messages.disclaimer)}
        </TypographyCaption>
        {gallery.visualdx_url ? (
          <ExternalTextLink href={gallery.visualdx_url}>
            {intl.formatMessage(messages.browseAll)}
          </ExternalTextLink>
        ) : null}
      </div>
    </div>
  );
};
